/**
 * Corrects a gap in Shopify's native collection filtering: the native
 * Size/Color/Availability filters each match if ANY variant of a product
 * satisfies that facet independently, so "Size: L" + "Availability: In
 * stock" will show a product whose L is sold out as long as some other
 * size is in stock. This re-checks, per product, whether a SINGLE variant
 * satisfies every active facet at once, and hides products that don't.
 */
(function () {
  const FACET_LABELS = {
    size: 'size',
    color: 'color',
    colour: 'color',
    length: 'length',
    hand: 'hand',
  };

  function getGroupFacet(groupEl) {
    const titleEl = groupEl.querySelector('.collection-filter__group-title');
    if (!titleEl) return null;
    const label = titleEl.textContent.trim().toLowerCase();
    if (FACET_LABELS[label]) return FACET_LABELS[label];
    if (label === 'availability') return 'availability';
    return null;
  }

  function getActiveConstraints() {
    const constraints = {
      size: new Set(),
      color: new Set(),
      length: new Set(),
      hand: new Set(),
      availability: null, // null | 'available' | 'unavailable'
    };

    document.querySelectorAll('[data-filter-group]').forEach((group) => {
      const facet = getGroupFacet(group);
      if (!facet) return;

      const checked = group.querySelectorAll('input[data-filter-checkbox]:checked');
      if (checked.length === 0) return;

      if (facet === 'availability') {
        checked.forEach((input) => {
          const label = input.closest('label');
          const text = label ? label.textContent.trim().toLowerCase() : '';
          constraints.availability = text.includes('out of stock') ? 'unavailable' : 'available';
        });
        return;
      }

      checked.forEach((input) => {
        // Merchant-curated Shopify "Filter Setting Group" values (e.g. "Blue
        // Family") submit an opaque gid:// id, not a real option value, so a
        // single variant's color/size/length can never equal it. Skip these
        // rather than treat every variant as a non-match - Shopify has
        // already filtered products for this facet server-side, so it's
        // safe to leave it unconstrained here.
        const label = input.closest('label');
        const isFamilyGroup =
          (label && label.hasAttribute('data-family-hint')) || input.value.indexOf('gid://shopify/') === 0;
        if (isFamilyGroup) return;
        constraints[facet].add(input.value);
      });
    });

    return constraints;
  }

  function hasActiveConstraints(constraints) {
    return (
      constraints.size.size > 0 ||
      constraints.color.size > 0 ||
      constraints.length.size > 0 ||
      constraints.hand.size > 0 ||
      constraints.availability !== null
    );
  }

  function variantSatisfies(variant, constraints) {
    if (constraints.size.size > 0 && !constraints.size.has(variant.size)) return false;
    if (constraints.color.size > 0 && !constraints.color.has(variant.color)) return false;
    if (constraints.length.size > 0 && !constraints.length.has(variant.length)) return false;
    if (constraints.hand.size > 0 && !constraints.hand.has(variant.hand)) return false;
    if (constraints.availability === 'available' && !variant.available) return false;
    if (constraints.availability === 'unavailable' && variant.available) return false;
    return true;
  }

  function productMatches(card, constraints) {
    const dataScript = card.querySelector('script.product-data');
    if (!dataScript) return true;

    let data;
    try {
      data = JSON.parse(dataScript.textContent);
    } catch (err) {
      return true;
    }

    const variants = (data && data.variants) || [];
    if (variants.length === 0) return true;

    return variants.some((variant) => variantSatisfies(variant, constraints));
  }

  function ensureEmptyState(container) {
    let emptyState = document.querySelector('[data-variant-filter-empty]');
    if (emptyState) return emptyState;

    emptyState = document.createElement('div');
    emptyState.setAttribute('data-variant-filter-empty', '');
    emptyState.className = 'collection-products__empty';
    emptyState.textContent = 'No products match the selected filters.';
    emptyState.style.display = 'none';
    container.insertAdjacentElement('beforebegin', emptyState);
    return emptyState;
  }

  function applyFilter() {
    const container = document.querySelector('.collection-products');
    if (!container) return;

    const constraints = getActiveConstraints();
    const cards = container.querySelectorAll('product-card[data-product-id]');

    if (!hasActiveConstraints(constraints)) {
      cards.forEach((card) => {
        card.removeAttribute('hidden');
      });
      const emptyState = document.querySelector('[data-variant-filter-empty]');
      if (emptyState) emptyState.style.display = 'none';
      return;
    }

    let visibleCount = 0;
    cards.forEach((card) => {
      const matches = productMatches(card, constraints);
      card.toggleAttribute('hidden', !matches);
      if (matches) visibleCount += 1;
    });

    const emptyState = ensureEmptyState(container);
    emptyState.style.display = visibleCount === 0 ? '' : 'none';
  }

  function init() {
    applyFilter();

    const container = document.querySelector('.collection-products');
    if (!container) return;

    // Re-run when infinite scroll appends more product cards.
    const observer = new MutationObserver((mutations) => {
      const addedCards = mutations.some((mutation) =>
        Array.from(mutation.addedNodes).some(
          (node) => node.nodeType === 1 && node.matches && node.matches('product-card[data-product-id]'),
        ),
      );
      if (addedCards) applyFilter();
    });

    observer.observe(container, { childList: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
